import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class HeaderBarPinnedServicesService {
  private static readonly storageKeyPrefix = 'Nimbus.HeaderBar.Services.PinnedTeamIds';

  // Agents may pin at most two services into the header bar for an at-a-glance queue view.
  public static readonly MAX_PINNED = 2;

  private readonly _pinnedTeamIds$ = new BehaviorSubject<string[]>(this.load());

  public readonly pinnedTeamIds$: Observable<string[]> = this._pinnedTeamIds$.asObservable();
  public readonly canPinMore$: Observable<boolean> = this.pinnedTeamIds$.pipe(
    map((ids) => ids.length < HeaderBarPinnedServicesService.MAX_PINNED),
  );

  public constructor(private readonly authenticationService: IAuthenticationService) {
    this.authenticationService.isAuthenticated$
      .pipe(
        filter(Boolean),
        map(() => this.storageKey()),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe(() => this._pinnedTeamIds$.next(this.load()));
  }

  public isPinned(teamId: string): boolean {
    return this._pinnedTeamIds$.getValue().includes(teamId);
  }

  public canPinMore(): boolean {
    return this._pinnedTeamIds$.getValue().length < HeaderBarPinnedServicesService.MAX_PINNED;
  }

  public toggle(teamId: string): void {
    const current = this._pinnedTeamIds$.getValue();
    if (current.includes(teamId)) {
      this.persist(current.filter((id) => id !== teamId));
      return;
    }
    // Silently ignore attempts to pin beyond the limit; the UI disables the control.
    if (current.length >= HeaderBarPinnedServicesService.MAX_PINNED) {
      return;
    }
    this.persist([...current, teamId]);
  }

  // Drops pins whose service the agent no longer has (membership removed) so the header stays clean.
  // An empty set means memberships have not loaded yet (teams$ is a BehaviorSubject seeded with [])
  // — pruning against it would wipe every stored pin on each navigation/refresh, so skip it. Stale
  // pins for such a set stay hidden anyway because the view filters chips by current membership.
  public prune(existingTeamIds: Set<string>): void {
    if (existingTeamIds.size === 0) {
      return;
    }
    const current = this._pinnedTeamIds$.getValue();
    const next = current.filter((id) => existingTeamIds.has(id));
    if (next.length !== current.length) {
      this.persist(next);
    }
  }

  private persist(ids: string[]): void {
    this._pinnedTeamIds$.next(ids);
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(ids));
    } catch {
      // Ignore storage write failures (private mode / quota); pins remain in memory.
    }
  }

  private load(): string[] {
    try {
      const raw = localStorage.getItem(this.storageKey());
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((id): id is string => typeof id === 'string').slice(0, HeaderBarPinnedServicesService.MAX_PINNED)
        : [];
    } catch {
      return [];
    }
  }

  private storageKey(): string {
    const userInfo = this.authenticationService.getUserInfo();
    const tenantId = userInfo?.tenantId ?? 'unknown';
    const userId = userInfo?.userId ?? 'unknown';
    return `${HeaderBarPinnedServicesService.storageKeyPrefix}.${tenantId}.${userId}`;
  }
}
